"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { CpmResults } from "@/components/tools/accounting/cpm-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type CpmError,
  type CpmResult,
  MAX_ACTIVITIES,
  type ParseMode,
  type ParseProblem,
  type PertSummary,
  parseActivities,
  pertSummary,
  schedule,
} from "@/lib/tool-kit/accounting/cpm";

/** English strings. A translated content file overrides these through `ui`. */
export const CRITICAL_PATH_CALCULATOR_STRINGS = {
  inputsHeading: "Your activities",
  modeLabel: "Durations",
  modeCpm: "One duration per activity (CPM)",
  modePert: "Three-point estimates (PERT)",
  unitLabel: "Time unit",
  unitDefault: "days",
  targetLabel: "Target completion time (optional)",
  targetHint: "In the same unit. Gives the chance of finishing by then.",
  cpmLabel: "Activities — one per line: ID, name, duration, predecessors",
  pertLabel:
    "Activities — one per line: ID, name, optimistic, most likely, pessimistic, predecessors",
  activitiesDetected: "one:{count} activity read|other:{count} activities read",
  cpmHint:
    "List predecessor IDs after the duration, separated by commas or spaces, or leave blank (or write -) for none. Leave the name empty with two commas if you have none. Use semicolons or tabs between fields if your numbers have decimal commas. A first line without numbers is read as a header; lines starting with # are ignored.",
  pertHint:
    "Estimates must satisfy optimistic ≤ most likely ≤ pessimistic. The expected time is (a + 4m + b) ÷ 6 and the standard deviation (b − a) ÷ 6.",
  cpmExample:
    "A, Site preparation, 3\nB, Foundation, 4, A\nC, Framing, 6, B\nD, Plumbing rough-in, 3, B\nE, Electrical rough-in, 4, B\nF, Roofing, 2, C\nG, Insulation, 2, D, E, F\nH, Drywall, 4, G\nI, Finishing, 3, H",
  pertExample:
    "A, Site preparation, 2, 3, 4\nB, Foundation, 3, 4, 7, A\nC, Framing, 4, 6, 8, B\nD, Plumbing rough-in, 2, 3, 4, B\nE, Electrical rough-in, 3, 4, 5, B\nF, Roofing, 1, 2, 3, C\nG, Insulation, 1, 2, 3, D, E, F\nH, Drywall, 3, 4, 5, G\nI, Finishing, 2, 3, 4, H",
  convention:
    "Every dependency is finish-to-start with no lag. Activities with zero total slack are critical. Free slack is how long an activity can slip without delaying any successor's earliest start.",
  resultsHeading: "Results",
  projectDuration: "Project duration",
  expectedDuration: "Expected project duration",
  criticalCount: "Critical activities",
  pathSigma: "Standard deviation of the path",
  confidence95: "Duration with 95% confidence",
  probability: "Chance of finishing by the target",
  criticalPath: "Critical path:",
  otherPaths:
    "one:(and {count} other critical path, below)|other:(and {count} other critical paths, below)",
  pathsTruncated: "Only the first critical paths are listed.",
  pertNote:
    "PERT treats the critical path {path} as approximately normal with mean {mean} and standard deviation {sigma}: the expected times add, and so do the variances of independent activities. If several paths are critical, the one with the largest variance is used.",
  pertNoteTarget:
    "PERT treats the critical path {path} as approximately normal with mean {mean} and standard deviation {sigma}; the target is z = {z} standard deviations from the mean. If several paths are critical, the one with the largest variance is used.",
  tableHeading: "Forward and backward pass",
  tableNote:
    "ES/EF: earliest start and finish. LS/LF: latest start and finish that do not delay the project. Slack = LS − ES. Critical rows are highlighted.",
  colId: "ID",
  colName: "Activity",
  colDuration: "Duration",
  colSigma: "σ",
  colPredecessors: "Predecessors",
  colEs: "ES",
  colEf: "EF",
  colLs: "LS",
  colLf: "LF",
  colSlack: "Total slack",
  colFree: "Free slack",
  colCritical: "Critical",
  yes: "Yes",
  no: "No",
  ganttHeading: "Schedule chart",
  ganttTitle: "Earliest schedule with slack",
  ganttDescription:
    "Bar chart of {count} activities on their earliest schedule over {duration} time units; {critical} are critical, with no slack.",
  legendCritical: "Critical",
  legendNormal: "Not critical",
  legendSlack: "Slack",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  errEmpty: "Enter at least one activity.",
  errTooMany: "Enter at most {max} activities.",
  errDuplicate:
    "The ID “{id}” is used more than once. Give each activity its own ID.",
  errSelfLoop: "Activity {id} lists itself as a predecessor.",
  errUnknown:
    "Activity {id} has a predecessor “{predecessor}” that is not in the list.",
  errCycle:
    "These activities depend on each other in a loop: {ids}. A project network cannot contain a cycle.",
  errFields: "Line {line} (“{text}”) has too few fields.",
  errDuration: "Line {line} (“{text}”) has a duration that is not a number.",
  errNegative: "Line {line} (“{text}”) has a negative duration.",
  errOrder:
    "Line {line} (“{text}”) must have optimistic ≤ most likely ≤ pessimistic.",
  errEmptyId: "Line {line} (“{text}”) has no ID.",
} as const;

export type CriticalPathCalculatorStrings =
  typeof CRITICAL_PATH_CALCULATOR_STRINGS;
type Strings = CriticalPathCalculatorStrings;

const PARSE_ERRORS: Record<ParseProblem, keyof Strings> = {
  fields: "errFields",
  duration: "errDuration",
  negative: "errNegative",
  order: "errOrder",
  emptyId: "errEmptyId",
};

function cpmMessage(error: CpmError, strings: Strings, max: string): string {
  switch (error.kind) {
    case "empty":
      return strings.errEmpty;
    case "tooMany":
      return t(strings.errTooMany, { max });
    case "duplicate":
      return t(strings.errDuplicate, { id: error.id });
    case "selfLoop":
      return t(strings.errSelfLoop, { id: error.id });
    case "unknown":
      return t(strings.errUnknown, {
        id: error.id,
        predecessor: error.predecessor,
      });
    default:
      return t(strings.errCycle, { ids: error.ids.join(", ") });
  }
}

type Outcome =
  | { problem: string }
  | {
      problem: null;
      result: CpmResult;
      pert: PertSummary | null;
      hasTarget: boolean;
    };

export default function CriticalPathCalculatorWidget() {
  const strings = useToolUi(CRITICAL_PATH_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<ParseMode>("cpm");
  const [cpmText, setCpmText] = useState<string>(
    CRITICAL_PATH_CALCULATOR_STRINGS.cpmExample
  );
  const [pertText, setPertText] = useState<string>(
    CRITICAL_PATH_CALCULATOR_STRINGS.pertExample
  );
  const [unit, setUnit] = useState<string>(
    CRITICAL_PATH_CALCULATOR_STRINGS.unitDefault
  );
  const [target, setTarget] = useState("26");

  const modes: Option<ParseMode>[] = [
    { value: "cpm", label: strings.modeCpm },
    { value: "pert", label: strings.modePert },
  ];

  function evaluate(): Outcome {
    const parsed = parseActivities(
      mode === "cpm" ? cpmText : pertText,
      mode,
      parseNumberInput
    );
    if (parsed.problem) {
      const { line, text, reason } = parsed.problem;
      return {
        problem: t(strings[PARSE_ERRORS[reason]], { line: fmt(line), text }),
      };
    }
    const result = schedule(parsed.activities);
    if ("error" in result) {
      return {
        problem: cpmMessage(result.error, strings, fmt(MAX_ACTIVITIES)),
      };
    }
    const targetValue = mode === "pert" ? parseNumberInput(target) : null;
    return {
      problem: null,
      result,
      pert: mode === "pert" ? pertSummary(result, targetValue) : null,
      hasTarget: targetValue !== null,
    };
  }
  const outcome = evaluate();
  const counted =
    outcome.problem === null ? outcome.result.activities.length : 0;

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="cpm-mode"
            label={strings.modeLabel}
            onChange={setMode}
            options={modes}
            value={mode}
          />
          <TextField
            id="cpm-unit"
            label={strings.unitLabel}
            onChange={setUnit}
            value={unit}
          />
          {mode === "pert" ? (
            <TextField
              hint={strings.targetHint}
              id="cpm-target"
              label={strings.targetLabel}
              numeric
              onChange={setTarget}
              value={target}
            />
          ) : null}
        </div>
        {mode === "cpm" ? (
          <TextAreaField
            hint={strings.cpmHint}
            id="cpm-cpm-text"
            label={strings.cpmLabel}
            onChange={setCpmText}
            rows={10}
            value={cpmText}
          />
        ) : (
          <TextAreaField
            hint={strings.pertHint}
            id="cpm-pert-text"
            label={strings.pertLabel}
            onChange={setPertText}
            rows={10}
            value={pertText}
          />
        )}
        <p aria-live="polite" className={HINT}>
          {counted > 0
            ? tPlural(strings.activitiesDetected, locale, counted, {
                count: fmt(counted),
              })
            : ""}
        </p>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {outcome.problem === null ? (
        <CpmResults
          hasTarget={outcome.hasTarget}
          pert={outcome.pert}
          result={outcome.result}
          strings={strings}
          unit={unit.trim()}
        />
      ) : (
        <Notice>{outcome.problem}</Notice>
      )}
    </div>
  );
}
